/**
 * MetaInfoSections — renders the "Loose relationships" and "JSON columns"
 * lists for a single MetaInfo record. Used by both the per-database manager
 * dialog and the global Settings → Meta Info section, so the listing/remove
 * UX stays in sync across surfaces.
 */

import type {
  EnumColumnDeclaration,
  EnumValueCache,
  JsonColumnDeclaration,
  JsonStructureCache,
  LooseRelationship,
  ReferenceTableDeclaration,
  SpatialColumnDeclaration,
  TableDisplayColumn,
} from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { EnumColumnsSection } from './EnumColumnsSection';
import { JsonColumnsSection } from './JsonColumnsSection';
import { SpatialColumnsSection } from './SpatialColumnsSection';
import { LooseRelationshipsList } from './LooseRelationshipsList';
import { openLooseRelationshipEditorDialog } from '../filters/looseRelationshipEditorDialogStore';

export type MetaInfoSectionsProps = {
  looseRelationships: LooseRelationship[];
  jsonColumns: JsonColumnDeclaration[];
  jsonStructureCache: JsonStructureCache[];
  enumColumns: EnumColumnDeclaration[];
  enumValueCache: EnumValueCache[];
  /** Reference-table flags + the shared label columns their names come from. */
  referenceTables?: ReferenceTableDeclaration[];
  tableDisplayColumns?: TableDisplayColumn[];
  spatialColumns: SpatialColumnDeclaration[];
  onRemoveLooseRel: (rel: LooseRelationship, groupLabel: string) => void;
  onRemoveJsonCol: (decl: JsonColumnDeclaration) => void;
  onRemoveEnumCol: (decl: EnumColumnDeclaration) => void;
  onRemoveSpatialCol: (decl: SpatialColumnDeclaration) => void;
  testIdPrefix?: string;
  /** Refetch context — forwarded to JsonColumnsSection so its "Details"
   * dialog can refresh the structure cache against the database. Also gates
   * the Details button on loose-FK rows (needs connection context to open
   * the editor). */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /**
   * Analytics source label propagated when the user opens the loose-FK
   * editor via the "Details" button — e.g. `'metainfo_manager_details'`,
   * `'settings_details'`.
   */
  detailsSourceLabel?: string;
};

export function MetaInfoSections({
  looseRelationships,
  jsonColumns,
  jsonStructureCache,
  enumColumns,
  enumValueCache,
  referenceTables,
  tableDisplayColumns,
  spatialColumns,
  onRemoveLooseRel,
  onRemoveJsonCol,
  onRemoveEnumCol,
  onRemoveSpatialCol,
  testIdPrefix = 'metainfo',
  connectionId,
  database,
  engine,
  detailsSourceLabel = 'metainfo_section_details',
}: MetaInfoSectionsProps) {
  const canShowDetails = !!(connectionId && database);
  return (
    <>
      <div className="text-xs font-semibold mt-1 mb-1">Loose relationships</div>
      <div
        className="flex flex-col gap-2 mb-3"
        data-testid={`${testIdPrefix}-list`}
      >
        <LooseRelationshipsList
          relationships={looseRelationships}
          onRemove={onRemoveLooseRel}
          onShowDetails={
            canShowDetails
              ? (r) => {
                  openLooseRelationshipEditorDialog({
                    connectionId: connectionId!,
                    database: database!,
                    engine,
                    source: {
                      schema: r.fromSchema,
                      table: r.fromTable,
                      name: r.fromColumn,
                      dataType: '',
                    },
                    existingRelationshipId: r.id,
                    source_label: detailsSourceLabel,
                  });
                }
              : undefined
          }
          testIdPrefix={testIdPrefix}
        />
      </div>

      <div className="text-xs font-semibold mt-1 mb-1">JSON columns</div>
      <div
        className="flex flex-col gap-1 mb-3"
        data-testid={`${testIdPrefix}-json-list`}
      >
        <JsonColumnsSection
          declarations={jsonColumns}
          structureCache={jsonStructureCache}
          onRemove={onRemoveJsonCol}
          connectionId={connectionId}
          database={database}
          engine={engine}
        />
      </div>

      <div className="text-xs font-semibold mt-1 mb-1">Enum columns</div>
      <div
        className="flex flex-col gap-1"
        data-testid={`${testIdPrefix}-enum-list`}
      >
        <EnumColumnsSection
          declarations={enumColumns}
          valueCache={enumValueCache}
          onRemove={onRemoveEnumCol}
          connectionId={connectionId}
          database={database}
          engine={engine}
          referenceTables={referenceTables}
          tableDisplayColumns={tableDisplayColumns}
        />
      </div>

      <div className="text-xs font-semibold mt-3 mb-1">Coordinate columns</div>
      <div
        className="flex flex-col gap-1"
        data-testid={`${testIdPrefix}-spatial-list`}
      >
        <SpatialColumnsSection
          declarations={spatialColumns}
          onRemove={onRemoveSpatialCol}
        />
      </div>
    </>
  );
}
